<template>
  <v-card class="dashboard-toolbar elevation-3 mb-4" color="white">
    <v-container fluid class="pa-3">
      <!-- Row 1: Stats and Actions -->
      <v-row align="center" no-gutters>
        <!-- Alert Summary Cards -->
        <v-col cols="auto" class="d-flex flex-wrap">
          <v-tooltip bottom v-for="alert in alertSummary" :key="alert.key">
            <template v-slot:activator="{ on, attrs }">
              <v-badge
                :value="isFilterActive(alert.name)"
                color="white"
                icon="mdi-check-circle"
                overlap
                offset-x="12"
                offset-y="12"
              >
                <v-card
                  v-bind="attrs"
                  v-on="on"
                  :class="[
                    'alert-card ma-1 pa-2 d-flex align-center',
                    { 'alert-active': alert.count > 0 },
                    { 'filter-selected': isFilterActive(alert.name) }
                  ]"
                  :color="alert.count > 0 ? alert.color : 'grey lighten-3'"
                  :dark="alert.count > 0"
                  @click="toggleAlertFilter(alert.name)"
                  :elevation="isFilterActive(alert.name) ? 8 : 1"
                  :style="{
                    cursor: 'pointer',
                    border: isFilterActive(alert.name) ? '3px solid #000' : '1px solid transparent',
                    transform: isFilterActive(alert.name) ? 'scale(1.1)' : 'scale(1)'
                  }"
                >
                  <v-icon v-if="isFilterActive(alert.name)" small class="mr-1">mdi-check-bold</v-icon>
                  <v-icon v-else small class="mr-1">{{ alert.icon }}</v-icon>
                  <span class="text-caption font-weight-medium">{{ alert.count }}</span>
                </v-card>
              </v-badge>
            </template>
            <span>
              {{ alert.tooltip }} ({{ alert.count }} kiosks)
              <br>
              <strong v-if="isFilterActive(alert.name)">✓ Filtre actif</strong>
              <em v-else>Cliquer pour filtrer</em>
            </span>
          </v-tooltip>
        </v-col>

        <v-spacer />

        <!-- Active filters indicator -->
        <v-col cols="auto" class="mx-2" v-if="selectedFilters.length > 0">
          <v-chip color="primary" small close @click:close="$emit('clear-filters')">
            <v-icon left small>mdi-filter-check</v-icon>
            {{ selectedFilters.length }} filtre(s) actif(s)
          </v-chip>
        </v-col>

        <!-- Kiosk counter -->
        <v-col cols="auto" class="mx-4">
          <v-chip color="primary" outlined>
            <v-icon left small>mdi-desktop-classic</v-icon>
            {{ filteredCount }} / {{ totalCount }} kiosks
          </v-chip>
        </v-col>

        <!-- Action buttons -->
        <v-col cols="auto">
          <v-btn-toggle dense class="mr-2">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn v-bind="attrs" v-on="on" @click="$emit('refresh-tickets')" :disabled="loading" small>
                  <v-icon small>mdi-folder-refresh-outline</v-icon>
                </v-btn>
              </template>
              <span>Rafraîchir les tickets Zoho</span>
            </v-tooltip>

            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn v-bind="attrs" v-on="on" @click="$emit('open-refresh-dialog')" :disabled="loading" small>
                  <v-icon small>mdi-refresh</v-icon>
                </v-btn>
              </template>
              <span>Générer un nouveau rapport</span>
            </v-tooltip>

            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn v-bind="attrs" v-on="on" @click="$emit('download')" small>
                  <v-icon small>mdi-download</v-icon>
                </v-btn>
              </template>
              <span>Exporter en Excel</span>
            </v-tooltip>

            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn v-bind="attrs" v-on="on" @click="$emit('open-info-dialog')" small>
                  <v-icon small>mdi-information</v-icon>
                </v-btn>
              </template>
              <span>État des services</span>
            </v-tooltip>
          </v-btn-toggle>
        </v-col>
      </v-row>

      <v-divider class="my-2" />

      <!-- Row 2: Filters -->
      <v-row align="center" no-gutters class="filter-row">
        <!-- Date picker -->
        <v-col cols="auto" class="d-flex align-center">
          <v-menu v-model="dateMenu" :close-on-content-click="true" transition="scale-transition" offset-y min-width="auto">
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                v-model="localDate"
                label="Date"
                prepend-icon="mdi-calendar"
                readonly
                v-bind="attrs"
                v-on="on"
                dense
                hide-details
                outlined
                class="filter-field date-field"
              />
            </template>
            <v-date-picker v-model="localDate" :allowed-dates="allowedDates" @input="dateMenu = false" />
          </v-menu>
        </v-col>

        <!-- Time select -->
        <v-col cols="auto" class="d-flex align-center ml-2">
          <v-select
            v-model="localTime"
            :items="availableTimes"
            item-text="formattedTime"
            item-value="_id"
            label="Heure"
            :disabled="!availableTimes.length"
            @change="$emit('time-select', $event)"
            dense
            hide-details
            outlined
            class="filter-field time-field"
          />
        </v-col>

        <!-- Search -->
        <v-col cols="auto" class="d-flex align-center ml-4">
          <v-text-field
            :value="search"
            @input="$emit('update:search', $event)"
            prepend-inner-icon="mdi-magnify"
            label="Rechercher"
            single-line
            hide-details
            dense
            outlined
            clearable
            class="filter-field search-field"
          />
        </v-col>

        <!-- Tags filter -->
        <v-col cols="auto" class="d-flex align-center ml-2">
          <v-select
            :value="selectedTags"
            @input="$emit('update:selectedTags', $event)"
            :items="availableTags"
            item-text="name"
            item-value="id"
            label="Tags"
            clearable
            multiple
            dense
            hide-details
            outlined
            class="filter-field tags-field"
          >
            <template v-slot:selection="{ item, index }">
              <v-chip v-if="index === 0" x-small>{{ item.name }}</v-chip>
              <span v-if="index === 1" class="grey--text text-caption">+{{ selectedTags.length - 1 }}</span>
            </template>
          </v-select>
        </v-col>

        <v-divider class="mx-4" vertical />

        <!-- Ticket filter -->
        <v-col cols="auto">
          <v-btn-toggle :value="ticketFilter" @change="$emit('update:ticketFilter', $event)" mandatory dense class="ticket-filter-group">
            <v-btn
              value="all"
              small
              :class="['ticket-btn-text', { 'ticket-btn-active ticket-btn-all': ticketFilter === 'all' }]"
              :outlined="ticketFilter !== 'all'"
            >
              <v-icon v-if="ticketFilter === 'all'" left small>mdi-check-bold</v-icon>
              <span class="ticket-btn-label">Tous</span>
            </v-btn>
            <v-btn
              value="with"
              small
              :color="ticketFilter === 'with' ? 'warning' : ''"
              :class="['ticket-btn-text', { 'ticket-btn-active': ticketFilter === 'with' }]"
              :outlined="ticketFilter !== 'with'"
            >
              <v-icon v-if="ticketFilter === 'with'" left small>mdi-check-bold</v-icon>
              <v-icon v-else left small>mdi-ticket</v-icon>
              <span class="ticket-btn-label">Avec ticket</span>
            </v-btn>
            <v-btn
              value="without"
              small
              :color="ticketFilter === 'without' ? 'success' : ''"
              :class="['ticket-btn-text', { 'ticket-btn-active': ticketFilter === 'without' }]"
              :outlined="ticketFilter !== 'without'"
            >
              <v-icon v-if="ticketFilter === 'without'" left small>mdi-check-bold</v-icon>
              <v-icon v-else left small>mdi-ticket-outline</v-icon>
              <span class="ticket-btn-label">Sans ticket</span>
            </v-btn>
          </v-btn-toggle>
        </v-col>

        <v-spacer />

        <!-- XOR mode -->
        <v-col cols="auto">
          <v-switch
            :input-value="useXorMode"
            @change="$emit('update:useXorMode', $event)"
            :label="useXorMode ? 'Mode OU' : 'Mode ET'"
            hide-details
            dense
            class="mt-0"
          />
        </v-col>

        <!-- Clear filters -->
        <v-col cols="auto">
          <v-btn text small color="grey" @click="$emit('clear-filters')">
            <v-icon left small>mdi-filter-off</v-icon>
            Réinitialiser
          </v-btn>
        </v-col>
      </v-row>
    </v-container>
  </v-card>
</template>

<script>
export default {
  name: 'DashboardToolbar',
  props: {
    // Data counts
    totalCount: { type: Number, default: 0 },
    filteredCount: { type: Number, default: 0 },
    alertCounts: { type: Object, default: () => ({}) },
    // Filters
    date: { type: String, default: null },
    time: { type: String, default: null },
    search: { type: String, default: '' },
    selectedTags: { type: Array, default: () => [] },
    availableTags: { type: Array, default: () => [] },
    availableReports: { type: Array, default: () => [] },
    ticketFilter: { type: String, default: 'all' },
    selectedFilters: { type: Array, default: () => [] },
    useXorMode: { type: Boolean, default: false },
    loading: { type: Boolean, default: false }
  },
  data() {
    return {
      dateMenu: false,
      alertTypes: [
        { key: 'salesAlert', name: 'Alerte vente', color: 'purple', icon: 'mdi-cart-off', tooltip: 'Aucune vente depuis 3 jours' },
        { key: 'tpaAlert', name: 'Alerte TPA', color: 'green', icon: 'mdi-credit-card-off', tooltip: 'TPA non communiquant depuis 3 jours' },
        { key: 'pingAlert', name: 'Alerte ping nuage', color: 'blue', icon: 'mdi-wifi-off', tooltip: 'Ping nuage > 3 jours' },
        { key: 'cancelledSalesAlert', name: 'Alerte vente annulée', color: 'yellow darken-2', icon: 'mdi-cart-remove', tooltip: 'Ventes annulées > 30%' },
        { key: 'rackAlert', name: 'Com rack KO', color: 'orange', icon: 'mdi-server-off', tooltip: 'Communication rack NOK' },
        { key: 'voltageAlert', name: 'Tension faible', color: 'red', icon: 'mdi-flash-alert', tooltip: 'Tension < 11.7V' },
        { key: 'motherboardFreezeAlert', name: 'Carte mère freeze', color: 'cyan', icon: 'mdi-snowflake-alert', tooltip: 'Carte mère freeze détecté' },
        { key: 'fuseAlert', name: 'Électronique (fusible)', color: 'pink', icon: 'mdi-fuse-alert', tooltip: 'Pas de ping nuit > 7 jours' },
        { key: 'batteryAlert', name: 'Batterie', color: 'deep-orange', icon: 'mdi-battery-alert', tooltip: 'Batterie faible la nuit' }
      ]
    };
  },
  computed: {
    localDate: {
      get() { return this.date; },
      set(value) { this.$emit('update:date', value); }
    },
    localTime: {
      get() { return this.time; },
      set(value) { this.$emit('update:time', value); }
    },
    availableDates() {
      return this.availableReports.map(item => item._id);
    },
    allowedDates() {
      return date => this.availableDates.includes(date);
    },
    availableTimes() {
      const match = this.availableReports.find(item => item._id === this.date);
      if (!match) return [];
      return match.reports.map(r => ({
        ...r,
        formattedTime: new Date(r.date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      }));
    },
    alertSummary() {
      return this.alertTypes.map(alert => ({
        ...alert,
        count: this.alertCounts[alert.key] || 0
      }));
    }
  },
  methods: {
    toggleAlertFilter(filterName) {
      const currentFilters = [...this.selectedFilters];
      const index = currentFilters.indexOf(filterName);
      if (index > -1) {
        currentFilters.splice(index, 1);
      } else {
        currentFilters.push(filterName);
      }
      this.$emit('update:selectedFilters', currentFilters);
    },
    isFilterActive(filterName) {
      return this.selectedFilters.includes(filterName);
    }
  }
};
</script>

<style scoped>
.dashboard-toolbar {
  position: sticky;
  top: 0;
  z-index: 10;
}

.alert-card {
  min-width: 50px;
  transition: all 0.2s ease;
  cursor: pointer;
}

.alert-card:hover {
  transform: scale(1.08) !important;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
}

.alert-active {
  font-weight: bold;
}

.filter-selected {
  animation: pulse 1.5s infinite;
  box-shadow: 0 0 0 3px rgba(0, 0, 0, 0.3) !important;
}

@keyframes pulse {
  0% {
    box-shadow: 0 0 0 3px rgba(0, 0, 0, 0.3);
  }
  50% {
    box-shadow: 0 0 0 6px rgba(0, 0, 0, 0.1);
  }
  100% {
    box-shadow: 0 0 0 3px rgba(0, 0, 0, 0.3);
  }
}

/* Ticket filter styles */
.ticket-filter-group {
  border: 2px solid #e0e0e0;
  border-radius: 4px;
  padding: 2px;
  background: #fafafa;
}

.ticket-btn-text {
  text-transform: none !important;
}

.ticket-btn-label {
  font-size: 14px !important;
  font-weight: 500;
}

.ticket-btn-active {
  font-weight: bold !important;
  transform: scale(1.05);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2) !important;
  border: 2px solid #000 !important;
}

.ticket-btn-active .ticket-btn-label {
  font-weight: 700 !important;
}

.ticket-btn-all.ticket-btn-active {
  background-color: #e0e0e0 !important;
  color: #000 !important;
}

.ticket-btn-all.ticket-btn-active .v-icon {
  color: #000 !important;
}

/* Filter fields styles */
.filter-row {
  min-height: 48px;
}

.filter-field {
  margin: 0 !important;
}

.filter-field >>> .v-input__slot {
  min-height: 36px !important;
}

.filter-field >>> .v-label {
  top: 8px !important;
}

.filter-field >>> .v-input__prepend-outer {
  margin-top: 0 !important;
  margin-right: 8px !important;
}

.date-field {
  width: 160px;
}

.time-field {
  width: 100px;
}

.search-field {
  width: 200px;
}

.tags-field {
  width: 180px;
}
</style>
